import { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

/**
 * InputMask leve, sem dependência externa, sem uso de findDOMNode.
 * Aceita máscara no formato "999.999.999-99" ou "(99)99999-9999".
 * Mantém estado interno (apenas dígitos) para evitar problemas de cursor
 * causados por componente controlado puro.
 * Suporta alsoShowMask (alwaysShowMask) similar ao react-input-mask.
 */
export default function InputMask({
  mask,
  maskChar = " ",
  alwaysShowMask = false,
  value,
  onChange,
  type = "text",
  disabled,
  readOnly,
  autoComplete,
  ...rest
}) {
  const { chars, charMap } = useMemo(() => {
    const ch = mask.split("");
    const map = ch.map((c) => {
      if (c === "9") return "digit";
      if (c === "a") return "letter";
      if (c === "*") return "alphanum";
      return "fixed";
    });
    return { chars: ch, charMap: map };
  }, [mask]);

  const digitsOnly = (s) => (s || "").replace(/\D/g, "");

  const formatValue = (raw) => {
    // Sem dígitos: campo vazio, sem placeholder visível.
    if (!raw) return "";
    let out = "";
    let rawIdx = 0;
    for (let i = 0; i < chars.length; i++) {
      if (charMap[i] !== "fixed") {
        if (rawIdx >= raw.length) break;
        out += raw[rawIdx];
        rawIdx++;
      } else {
        if (rawIdx < raw.length) {
          out += chars[i];
        }
      }
    }
    return out;
  };

  // Extrai apenas os dígitos do valor controlado (que pode estar formatado
  // pelo pai, ex.: "(11)91234-5678" ou puro "11912345678").
  const extractDigits = (raw) => {
    if (!raw) return "";
    // Se o pai mandou formatado, removemos apenas dígitos e mantemos
    // o mesmo comportamento que stripFixed fazia.
    const stripped = (() => {
      let out = "";
      let rawIdx = 0;
      for (let i = 0; i < chars.length; i++) {
        const isFixed = charMap[i] === "fixed";
        if (isFixed) {
          if (raw[rawIdx] === chars[i]) rawIdx++;
          continue;
        }
        if (rawIdx >= raw.length) break;
        out += raw[rawIdx];
        rawIdx++;
      }
      return out;
    })();
    // stripFixed pode ter retornado string com não-dígitos quando o pai
    // mandou algo inconsistente. Garante só dígitos.
    return digitsOnly(stripped || raw);
  };

  // Estado interno: armazena SOMENTE dígitos (evita loop com formatador do pai)
  const [internalDigits, setInternalDigits] = useState(() => extractDigits(value));
  const inputRef = useRef(null);
  const lastSelectionRef = useRef(null);

  // Sincroniza estado interno quando o `value` controlado muda externamente
  // (ex.: reset, carregar dados do servidor, setFormData inicial).
  useEffect(() => {
    const fromProp = extractDigits(value);
    if (fromProp !== internalDigits) {
      setInternalDigits(fromProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const display = formatValue(internalDigits);

  // Calcula a posição do cursor no display com base no índice do dígito.
  // Ex.: 3 dígitos => "(31)9" => cursor após o "9" => posição 5.
  // Se for 0 dígitos => "" => posição 0.
  const cursorFromDigits = (numDigits) => {
    if (numDigits <= 0) return 0;
    let digitsSeen = 0;
    for (let i = 0; i < charMap.length; i++) {
      if (charMap[i] !== "fixed") {
        digitsSeen++;
        if (digitsSeen === numDigits) return i + 1;
      }
    }
    return display.length;
  };

  const restoreCursor = () => {
    const el = inputRef.current;
    if (!el) return;
    const target = lastSelectionRef.current;
    if (!target) return;
    let { start, end } = target;
    // Se o cursor (sem seleção) está sobre um caractere fixo,
    // reposiciona com base no número de dígitos para que o usuário
    // nunca fique preso em ")" , "-" ou "(" .
    if (start === end) {
      // Calcula quantos dígitos existem ANTES da posição atual do cursor
      let digitsBefore = 0;
      for (let i = 0; i < Math.min(start, display.length); i++) {
        if (charMap[i] !== "fixed") digitsBefore++;
      }
      // Se o cursor está sobre um fixo, joga para o próximo dígito
      if (start < display.length && charMap[start] === "fixed") {
        start = cursorFromDigits(digitsBefore + 1);
      }
      // Se o cursor está depois do último caractere mas o último é fixo,
      // joga para depois do último dígito.
      else if (start === display.length && display.length > 0 &&
               charMap[display.length - 1] === "fixed") {
        start = cursorFromDigits(digitsBefore);
      }
    }
    try {
      el.setSelectionRange(start, end);
    } catch (_) {
      /* ignore */
    }
  };

  const handleChange = (e) => {
    const el = e.target;
    const newVisible = el.value;
    const newDigits = digitsOnly(newVisible).slice(0, countEditableSlots());

    // Conta dígitos até a posição do cursor antes da mudança (input cru).
    // Usamos a posição do cursor no value cru do navegador para descobrir
    // onde o usuário está "editando" de fato.
    const rawCursor = el.selectionStart || 0;
    let digitsBefore = 0;
    for (let i = 0; i < Math.min(rawCursor, newVisible.length); i++) {
      if (/\d/.test(newVisible[i])) digitsBefore++;
    }
    // Se o cursor está sobre um fixo, isso ainda conta corretamente.
    // Caso especial: cursor no fim após um fixo => devemos considerar
    // que o usuário está "após" o último dígito (próximo slot).
    if (digitsBefore > newDigits.length) {
      digitsBefore = newDigits.length;
    }

    // Salva a posição desejada do cursor no display formatado, baseada
    // em quantos dígitos estão à esquerda.
    const desiredCursor = cursorFromDigits(digitsBefore);
    lastSelectionRef.current = { start: desiredCursor, end: desiredCursor };

    setInternalDigits(newDigits);

    if (onChange) {
      onChange({
        target: {
          name: rest.name,
          value: newDigits,
        },
      });
    }

    requestAnimationFrame(restoreCursor);
  };

  const countEditableSlots = () => {
    let n = 0;
    for (let i = 0; i < charMap.length; i++) {
      if (charMap[i] !== "fixed") n++;
    }
    return n;
  };

  // Quando o usuário pressiona Backspace ou Delete e o cursor está sobre
  // um caractere fixo (ex.: "(" , ")" , "-" ), ajustamos manualmente o
  // cursor para um dígito vizinho antes do navegador processar a tecla.
  // Isso garante que backspace/delete sempre apague um dígito real.
  const handleKeyDown = (e) => {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: end } = el;
    if (s !== end) return; // tem seleção, comportamento padrão
    const key = e.key;
    if (key !== "Backspace" && key !== "Delete") return;

    // Calcula a posição do cursor no display atual
    const pos = s;
    if (pos < 0 || pos > display.length) return;

    if (key === "Backspace") {
      // Se está sobre um fixo, pula para o dígito anterior
      if (pos > 0 && pos <= display.length && charMap[pos - 1] === "fixed") {
        e.preventDefault();
        let p = pos - 1;
        while (p > 0 && charMap[p - 1] === "fixed") p--;
        try {
          el.setSelectionRange(p, p);
        } catch (_) {
          /* ignore */
        }
      } else if (pos === display.length && display.length > 0 &&
                 charMap[display.length - 1] === "fixed") {
        // Cursor no fim após um fixo (ex.: após "-" ), pula para o último dígito
        e.preventDefault();
        let p = display.length - 1;
        while (p > 0 && charMap[p] === "fixed") p--;
        try {
          el.setSelectionRange(p, p);
        } catch (_) {
          /* ignore */
        }
      }
    } else if (key === "Delete") {
      // Se está sobre um fixo, pula para o próximo dígito
      if (pos < display.length && charMap[pos] === "fixed") {
        e.preventDefault();
        let p = pos + 1;
        while (p < display.length && charMap[p] === "fixed") p++;
        try {
          el.setSelectionRange(p, p);
        } catch (_) {
          /* ignore */
        }
      }
    }
  };

  const handleSelect = (e) => {
    lastSelectionRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
  };

  const finalValue = alwaysShowMask
    ? display + maskChar.repeat(Math.max(0, mask.length - display.length))
    : display;

  return (
    <input
      {...rest}
      ref={inputRef}
      type={type}
      disabled={disabled}
      readOnly={readOnly}
      autoComplete={autoComplete}
      value={finalValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onSelect={handleSelect}
    />
  );
}

InputMask.propTypes = {
  mask: PropTypes.string.isRequired,
  maskChar: PropTypes.string,
  alwaysShowMask: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  autoComplete: PropTypes.string,
};
